/* 3OS 275 · one serial write rail for every Shopify cart surface.
   A cancelled fetch does not cancel a Shopify write, so mutations are never
   aborted. They are executed one at a time and every consumer receives the
   same confirmed server response before the next mutation starts. */
(function (root) {
  'use strict';

  class ThreeOlivesCartTransactionQueue {
    constructor () {
      this.jobs = [];
      this.running = false;
      this.state = {
        phase: 'idle',
        revision: 0,
        pending: 0,
        active: null,
        confirmedCart: null,
        lastError: null
      };
    }

    snapshot () {
      return {
        phase: this.state.phase,
        revision: this.state.revision,
        pending: this.state.pending,
        active: this.state.active,
        confirmedCart: this.state.confirmedCart,
        lastError: this.state.lastError
      };
    }

    emit () {
      if (typeof document === 'undefined' || typeof document.dispatchEvent !== 'function') return;
      if (typeof CustomEvent !== 'function') return;
      this.paintDocumentState();
      document.dispatchEvent(new CustomEvent('three-olives:cart-state', { detail: this.snapshot() }));
    }

    paintDocumentState () {
      if (!document.documentElement || typeof document.querySelectorAll !== 'function') return;
      const busy = this.state.phase === 'updating';
      document.documentElement.setAttribute('data-cart-transaction-state', this.state.phase);
      document.querySelectorAll('[data-tos-wallet-dock], #main-cart-footer, cart-drawer, [data-cart-page]').forEach((region) => {
        region.setAttribute('aria-busy', String(busy));
        if ('inert' in region) region.inert = busy;
      });
      document.querySelectorAll('[name="checkout"]').forEach((control) => {
        if (busy) {
          if (!control.disabled) control.dataset.tosTransactionDisabled = 'temporary';
          control.disabled = true;
          control.setAttribute('aria-disabled', 'true');
        } else if (control.dataset.tosTransactionDisabled === 'temporary') {
          control.disabled = false;
          control.removeAttribute('aria-disabled');
          delete control.dataset.tosTransactionDisabled;
        }
      });
    }

    isBusy () {
      return this.state.pending > 0;
    }

    enqueue (task, meta) {
      if (typeof task !== 'function') return Promise.reject(new TypeError('cart_transaction_task_required'));
      this.state.pending += 1;
      this.state.phase = 'updating';
      this.state.lastError = null;
      this.emit();

      const promise = new Promise((resolve, reject) => {
        this.jobs.push({ task: task, meta: meta || {}, resolve: resolve, reject: reject });
      });
      this.drain();
      return promise;
    }

    drain () {
      if (this.running || this.jobs.length === 0) return;
      const job = this.jobs.shift();
      const revision = ++this.state.revision;
      this.running = true;
      this.state.active = { revision: revision, ...job.meta };
      this.state.phase = 'updating';
      this.emit();

      let result;
      try {
        // Intentionally invoke the first task now. This preserves immediate UI
        // feedback while the running flag keeps every later write serialized.
        result = job.task({ revision: revision, meta: job.meta });
      } catch (error) {
        result = Promise.reject(error);
      }

      Promise.resolve(result)
        .then((confirmed) => {
          if (confirmed?.confirmedCart && typeof confirmed.confirmedCart === 'object') {
            this.state.confirmedCart = confirmed.confirmedCart;
          } else if (confirmed && typeof confirmed === 'object') {
            this.state.confirmedCart = confirmed;
          }
          this.state.lastError = null;
          this.finish(job);
          job.resolve(confirmed);
          this.drain();
        }, (error) => {
          // A failed mutation can still have a trustworthy recovery readback.
          // Preserve that server-confirmed cart for every later consumer, while
          // keeping the transaction in an explicit error state.
          if (error?.confirmedCart && typeof error.confirmedCart === 'object') {
            this.state.confirmedCart = error.confirmedCart;
          }
          this.state.lastError = error || new Error('cart_transaction_failed');
          this.state.phase = 'error';
          this.finish(job);
          job.reject(error);
          this.drain();
        });
    }

    finish () {
      this.state.pending = Math.max(0, this.state.pending - 1);
      this.state.active = null;
      this.running = false;
      if (this.state.pending > 0) this.state.phase = 'updating';
      else if (!this.state.lastError) this.state.phase = 'idle';
      this.emit();
    }
  }

  root.ThreeOlivesCartTransactionQueue = ThreeOlivesCartTransactionQueue;
  if (!root.__THREE_OLIVES_CART_TRANSACTIONS__) {
    root.__THREE_OLIVES_CART_TRANSACTIONS__ = new ThreeOlivesCartTransactionQueue();
  }
})(typeof window !== 'undefined' ? window : globalThis);
